import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
const API_URL = 'http://localhost:7071';
import { Card, CardContent, Typography, Box, Dialog, DialogTitle, DialogContent, IconButton, List, ListItem, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import {
  Mail,
  Phone,
  MapPin,
  Star,
  Globe,
  Facebook,
  Instagram,
  Smartphone,
  Image
} from 'lucide-react';

export default function PropertyCard({ property }) {
  const [logOpen, setLogOpen] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const SkeletonBlock = ({ style = {} }) => (
    <motion.div
      initial={{ backgroundPositionX: '100%' }}
      animate={{ backgroundPositionX: '-100%' }}
      transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
      style={{
        width: '100%',
        height: '20px',
        borderRadius: '6px',
        backgroundImage: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
        backgroundSize: '200% 100%',
        backgroundRepeat: 'no-repeat',
        marginBottom: '12px',
        ...style
      }}
    />
  );

  useEffect(() => {
    if (logOpen) {
      axios.get('http://localhost:7071/api/tripadvisor/sync-log')
        .then(res => setSyncLogs(res.data))
        .catch(() => setSyncLogs([]));
    }
  }, [logOpen]);

  if (!property || !property.name) {
    return (
      <Card variant="outlined" sx={{ margin: 2, padding: 2 }}>
        <SkeletonBlock />
        <SkeletonBlock />
        <SkeletonBlock />
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ margin: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          {property.logo && (
            <img
              src={property.logo.startsWith('http') ? property.logo : `${API_URL}${property.logo}`}
              alt="Property Logo"
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: 'white',
                padding: 4,
                objectFit: 'contain',
                boxShadow: '0 0 6px rgba(0,0,0,0.2)'
              }}
            />
          )}
          <Box>
            <Typography variant="h6" fontWeight="bold">{property.name}</Typography>
            {property._id && (
              <Typography variant="caption" color="text.secondary">
                ID: {property._id}
              </Typography>
            )}
          </Box>
        </Box>

        {property.images && property.images.length > 0 && (
          <img
            src={property.images[0].startsWith('http') ? property.images[0] : `${API_URL}${property.images[0]}`}
            alt="Feature"
            style={{ width: '100%', maxHeight: '400px', objectFit: 'cover', borderRadius: 8 }}
          />
        )}

        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            <MapPin size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {property.address}
          </Typography>
          <Typography variant="body2">
            <Mail size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {property.email}
          </Typography>
          <Typography variant="body2">
            <Phone size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {property.phone}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <Star size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {property.hotelStarRating || 'Not Rated'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight="bold">
          <Image size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          About
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>{property.description}</Typography>

        {property.images && property.images.length > 1 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
              {property.images.slice(1).map((img, idx) => (
                <img
                  key={idx}
                  src={img.startsWith('http') ? img : `${API_URL}${img}`}
                  alt={`Gallery ${idx + 1}`}
                  style={{ width: '200px', height: '130px', objectFit: 'cover', borderRadius: 6 }}
                />
              ))}
            </Box>
          </>
        )}

        {property.socialLinks && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box mt={2}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Social Media
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {property.socialLinks.facebook && (
                  <Typography variant="body2">
                    <Facebook size={16} style={{ marginRight: 4 }} /> Facebook: <a href={property.socialLinks.facebook} target="_blank" rel="noopener noreferrer">{property.socialLinks.facebook}</a>
                  </Typography>
                )}
                {property.socialLinks.instagram && (
                  <Typography variant="body2">
                    <Instagram size={16} style={{ marginRight: 4 }} /> Instagram: <a href={property.socialLinks.instagram} target="_blank" rel="noopener noreferrer">{property.socialLinks.instagram}</a>
                  </Typography>
                )}
                {property.socialLinks.website && (
                  <Typography variant="body2">
                    <Globe size={16} style={{ marginRight: 4 }} /> Website: <a href={property.socialLinks.website} target="_blank" rel="noopener noreferrer">{property.socialLinks.website}</a>
                  </Typography>
                )}
                {property.socialLinks.whatsapp && (
                  <Typography variant="body2">
                    <Smartphone size={16} style={{ marginRight: 4 }} /> WhatsApp: <a href={`https://wa.me/${property.socialLinks.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">{property.socialLinks.whatsapp}</a>
                  </Typography>
                )}
              </Box>
            </Box>
          </>
        )}

        {property.googleMapEmbed && (
          <Box mt={3} dangerouslySetInnerHTML={{ __html: property.googleMapEmbed }} />
        )}
        {property.googleMapLink && (
          <Typography variant="body2" mt={1}>
            <MapPin size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            <a href={property.googleMapLink} target="_blank" rel="noopener noreferrer">Open in Google Maps</a>
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Box mt={2} color="text.secondary" fontSize="0.875rem">
          <Typography>Created At: {new Date(property.createdAt).toLocaleString()}</Typography>
          <Typography>Last Updated: {new Date(property.updatedAt).toLocaleString()}</Typography>
        </Box>

      </CardContent>
    </Card>
  );
}